import {Button, Menu03Icon, XCloseIcon} from '@translated/babele-react'
import * as DialogPrimitives from '@radix-ui/react-dialog'
import clsx from 'clsx'
import {useState, memo, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {navItems} from './utils'
import {LaraLogoExtended} from '~/ui/LaraLogoExtended'
import {TAppLink, TLink} from '~/features/TLink'
import SupportButton from './SupportButton'
import {useNavigation} from 'react-router'

export const MobileMenu = memo(({isAuthenticated}: {isAuthenticated: boolean}) => {
  const [isOpen, setIsOpen] = useState(false)

  const {t} = useTranslation()
  const {state} = useNavigation()

  // Close the menu when the user navigates to a new page
  useEffect(() => {
    if (state === 'idle') setIsOpen(false)
  }, [state])

  return (
    <DialogPrimitives.Root open={isOpen} onOpenChange={setIsOpen}>
      <DialogPrimitives.Trigger asChild data-mobile-menu="true">
        <Button
          aria-label="Main menu trigger"
          variant={{mode: 'ghost'}}
          className={clsx('!bg-neutral-25/1 !text-neutral-25 hover:!bg-neutral-25/2 md:hidden')}
        >
          <Menu03Icon />
        </Button>
      </DialogPrimitives.Trigger>
      <DialogPrimitives.Portal>
        <DialogPrimitives.Overlay className="fixed inset-0 z-10 bg-transparent" />
        <DialogPrimitives.Content
          data-theme="translated-light"
          aria-describedby={undefined}
          className={clsx(
            'fixed inset-0',
            'animate-fade-in outline-none',
            'bg-laraFull z-50 flex w-full flex-col'
          )}
        >
          <div
            data-theme="translated-light"
            className={clsx(
              'w-full px-3 py-4',
              'border-b-1 border-neutral-100/2',
              'flex items-center justify-between gap-1.5'
            )}
          >
            <LaraLogoExtended />
            <DialogPrimitives.Close asChild>
              <Button
                aria-label="Close mobile menu"
                variant={{mode: 'ghost'}}
                className={clsx('!bg-neutral-25/1 !text-neutral-25 hover:!bg-neutral-25/2')}
                onClick={() => {
                  setIsOpen(false)
                }}
              >
                <XCloseIcon />
              </Button>
            </DialogPrimitives.Close>
          </div>
          <div className="flex grow flex-col justify-between">
            <DialogPrimitives.DialogTitle className="sr-only">
              {t('mobile_menu_title')}
            </DialogPrimitives.DialogTitle>
            <div className="flex flex-col gap-6 p-6 py-12 text-start text-display-md font-medium text-neutral-25">
              {navItems.map(item =>
                item.isAppUrl ? (
                  <TAppLink key={item.id} to={item.link} className="text-neutral-100">
                    {t(item.label)}
                  </TAppLink>
                ) : (
                  <TLink isNav key={item.id} to={item.link} className="text-neutral-100">
                    {t(item.label)}
                  </TLink>
                )
              )}
              <a
                href="https://developers.laratranslate.com"
                target="_blank"
                rel="noreferrer"
                className="text-neutral-100"
              >
                {t('nav_pages.api_docs')}
              </a>
              <SupportButton
                className="w-fit text-neutral-100"
                onAfterClick={() => setIsOpen(false)}
              />
            </div>
            <div className="!bg-neutral-25/1">
              {isAuthenticated ? (
                <div className="flex h-[6rem] w-full items-center px-6">
                  <Button
                    variant={{size: 'lg'}}
                    render={
                      <TAppLink aria-label="Login" to="/translate" className="w-full">
                        {t('go_to_app')}
                      </TAppLink>
                    }
                    className="w-full !bg-neutral-25 !text-neutral-1000"
                  >
                    {t('go_to_app')}
                  </Button>
                </div>
              ) : (
                <div
                  className={clsx('h-[6rem] w-full items-center px-6', 'grid grid-cols-2 gap-3')}
                >
                  <Button
                    variant={{size: 'lg'}}
                    render={
                      <TAppLink aria-label="Login" to="/login">
                        {t('login')}
                      </TAppLink>
                    }
                    className="!bg-neutral-25/2"
                  >
                    {t('login')}
                  </Button>
                  <Button
                    variant={{size: 'lg'}}
                    render={
                      <TAppLink aria-label="Sign up" to="/sign-up">
                        {t('sign_up')}
                      </TAppLink>
                    }
                    className="!bg-neutral-25 !text-neutral-1000"
                  >
                    {t('sign_up')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogPrimitives.Content>
      </DialogPrimitives.Portal>
    </DialogPrimitives.Root>
  )
})
